import { useSceneEvents } from "@/components/common/scene-events-context";
import { AnalysesRenderer } from "@/components/r3f/renderers/analyses/analyses-renderer";
import { PointCloudRenderer } from "@/components/r3f/renderers/pointcloud-renderer";
import { useRealtimeRaycasting } from "@/hooks/use-real-time-raycasting";
import { PointCloudObject } from "@/object-cache";
import { selectZoomLensActive } from "@/store/measurement-tool-selector";
import { selectPointCloudAnalyses } from "@/store/point-cloud-analysis-tool-selector";
import { selectHiddenScans } from "@/store/selections-selectors";
import { useAppSelector } from "@/store/store-hooks";
import { selectObjectVisibility } from "@/store/view-options/view-options-selectors";
import { ViewObjectTypes } from "@/store/view-options/view-options-slice";
import { type ThreeEvent } from "@react-three/fiber";
import { isEqual } from "es-toolkit";
import { JSX, useCallback, useEffect } from "react";
import { useToolsAcquaintance } from "../tools/tools-acquaintance-context";

type FlyWalkPointCloudRendererProps = {
  /** Point cloud object in the current scene */
  pointCloud: PointCloudObject | null;
  /** Whether picking is enabled on the CAD model or not */
  pickingEnabled: boolean;
};

/** @returns a component to render an LOD point cloud in flywalk mode */
export function FlyWalkPointCloudRenderer({
  pointCloud,
  pickingEnabled,
}: FlyWalkPointCloudRendererProps): JSX.Element | null {
  const hiddenScans = useAppSelector(selectHiddenScans);

  const { invalidatePointCloudScene } = useSceneEvents();

  // Whenever the hidden scans change, we need to invalidate the point cloud scene
  // to re-draw the point cloud.
  useEffect(() => {
    invalidatePointCloudScene.emit();
  }, [hiddenScans, invalidatePointCloudScene]);

  const analyses = useAppSelector(
    selectPointCloudAnalyses(pointCloud?.iElement.id),
    isEqual,
  );

  const analysesVisible = useAppSelector(
    selectObjectVisibility(ViewObjectTypes.analyses),
  );
  const showAnalyses = analysesVisible && analyses.length > 0;

  const {
    clippingPlanes,
    onModelHovered,
    onModelClicked,
    onModelZoomed,
    activeToolAndPicking,
  } = useToolsAcquaintance();

  useRealtimeRaycasting(pointCloud, !activeToolAndPicking.isPickingToolActive);

  const onPointerMove = useCallback(
    (ev: ThreeEvent<PointerEvent>) => {
      if (pointCloud) {
        onModelHovered(ev, pointCloud.iElement.id);
      }
    },
    [pointCloud, onModelHovered],
  );

  const onClick = useCallback(
    (ev: ThreeEvent<MouseEvent>) => {
      if (pointCloud) {
        onModelClicked(ev, pointCloud.iElement.id);
      }
    },
    [pointCloud, onModelClicked],
  );

  const onWheel = useCallback(
    (ev: ThreeEvent<WheelEvent>) => {
      if (pointCloud) {
        onModelZoomed(ev, pointCloud.iElement.id);
      }
    },
    [pointCloud, onModelZoomed],
  );

  // If the zoom lens is active, we disable clicking on the point cloud,
  // because the clicks on the zoom lens are already handled in the
  // useZoomLens hook by onPointerUp
  const zoomLensOn = useAppSelector(selectZoomLensActive);

  if (!pointCloud) {
    return null;
  }

  return (
    <>
      <PointCloudRenderer
        pointCloud={pointCloud}
        hiddenScans={hiddenScans}
        onPointerMove={pickingEnabled ? onPointerMove : undefined}
        onClick={pickingEnabled && !zoomLensOn ? onClick : undefined}
        onWheel={pickingEnabled ? onWheel : undefined}
        clippingPlanes={clippingPlanes}
      />
      {showAnalyses && (
        <AnalysesRenderer
          analyses={analyses}
          pointCloud={pointCloud}
          clippingPlanes={clippingPlanes}
        />
      )}
    </>
  );
}
